/**
 * Single-select chip filter — used for "City" filter in the properties
 * list and similar low-cardinality categorical filters.
 *
 * Usage: pass options + the active value (or null for "all"), get the new
 * value back via onChange. The "All" chip is rendered automatically as
 * the first option.
 */
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'

export type ChipOption = {
  value: string
  label: string
  count?: number
}

type Props = {
  options: ChipOption[]
  value: string | null
  onChange: (value: string | null) => void
  allLabel?: string
  className?: string
}

export function FilterChipBar({
  options,
  value,
  onChange,
  allLabel = 'All',
  className,
}: Props) {
  return (
    <div className={`flex flex-wrap items-center gap-2 ${className ?? ''}`}>
      <Button
        type="button"
        size="sm"
        variant={value === null ? 'default' : 'outline'}
        onClick={() => onChange(null)}
      >
        {allLabel}
      </Button>
      {options.map((opt) => (
        <Button
          key={opt.value}
          type="button"
          size="sm"
          variant={value === opt.value ? 'default' : 'outline'}
          onClick={() => onChange(opt.value)}
          className="gap-2"
        >
          <span>{opt.label}</span>
          {typeof opt.count === 'number' && (
            <Badge
              variant={value === opt.value ? 'secondary' : 'outline'}
              className="px-1.5 text-[10px] font-normal"
            >
              {opt.count}
            </Badge>
          )}
        </Button>
      ))}
    </div>
  )
}
